<template>
  <div class="order-status-cell">
    <div class="status-row">
      <RefundTicket
        :status="orderDisplayStatus"
        :show-refund="showRefund"
        :refund-click="() => emit('refund')"
      />
      <div v-if="showReschedule" class="extra-actions">
        <Button type="link" size="small" @click="handleReschedule">改签</Button>
      </div>
      <div v-if="orderDisplayStatus === 1 && serial" class="extra-actions">
        <Popover placement="leftTop" overlay-class-name="refund-serial-popover">
          <template #title>退款信息</template>
          <template #content>
            <div class="pop-line">退款金额：￥{{ (record?.refundAmount ?? record?.amount ?? 0) / 100 }}</div>
            <div class="pop-line">平台交易号：{{ record?.platformTradeNo || '—' }}</div>
            <div class="pop-line">业务流水号：{{ serial }}</div>
            <div class="pop-line muted">
              {{
                record?.refundStatusText ||
                  '成功，退款将原路退回；若超过 7 个工作日未到账，请联系支付渠道或 12306。'
              }}
            </div>
          </template>
          <a class="serial-link">业务流水号</a>
        </Popover>
      </div>
    </div>

    <!-- 改签确认弹框 -->
    <Modal
      :visible="rescheduleModalVisible"
      title="提示"
      @cancel="rescheduleModalVisible = false"
      :footer="null"
      width="600px"
    >
      <div class="reschedule-confirm-modal">
        <div class="modal-icon">
          <QuestionCircleFilled style="font-size: 40px; color: #faad14;" />
        </div>
        <div class="modal-content">
          <p class="modal-text">
            您确定要改签吗？如有订餐饮或特产，请按规定到网站自行办理退订。
          </p>
          <p class="modal-warning">
            您改签或变更到站后车票的乘车日期在春运期间<span v-if="springFestivalPeriodText">（{{ springFestivalPeriodText }}）</span>，如再办理退票将按票面价格20%核收退票费。请您合理安排行程，更多改签规则请查看
            <a href="javascript:void(0)" @click="showRescheduleRules">《退改说明》</a>。
          </p>
        </div>
        <div class="modal-actions">
          <Button @click="rescheduleModalVisible = false">取消</Button>
          <Button type="primary" @click="confirmReschedule" :style="{ background: '#ff8c00', borderColor: '#ff8c00' }">
            确定
          </Button>
        </div>
      </div>
    </Modal>

    <RefundRescheduleRulesModal v-model:visible="rulesModalVisible" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { Button, Popover, Modal } from 'ant-design-vue'
import { QuestionCircleFilled } from '@ant-design/icons-vue'
import RefundTicket from '../refund-ticket'
import RefundRescheduleRulesModal from '@/components/refund-reschedule/RefundRescheduleRulesModal.vue'
import { getRefundSerial } from '@/utils/orderDisplay'
import {
  formatSpringFestivalRangesText,
  getSpringFestivalRanges
} from '@/utils/ticketRefundRescheduleRules'

const props = defineProps({
  record: { type: Object, default: null },
  /** 0 未完成 1 未出行 2 历史 */
  activeKey: { type: Number, default: 0 }
})

const emit = defineEmits(['refund'])

const router = useRouter()

const serial = computed(() => getRefundSerial(props.record))

/** 车票状态列：历史 Tab 优先展示 archiveStatus */
const orderDisplayStatus = computed(() => {
  const record = props.record
  if (!record) return undefined
  if (props.activeKey === 2 && record.archiveStatus != null && record.archiveStatus !== '') {
    return Number(record.archiveStatus)
  }
  if (record.rowSpan > 0 && record.orderStatus != null) {
    return record.orderStatus
  }
  return record.orderStatus ?? record.status
})

const showRefund = computed(() => {
  if (props.activeKey !== 1 || !props.record?.rowSpan) return false
  return !!props.record?.hasRefundablePassenger
})

/** 原订单乘客：已出票(3)/改签失败(23) 可在原单发起改签，已改签(50) 不可 */
const canReschedulePassengerOnOriginal = (record) => {
  if (!record) return false
  const s = record.passengerStatus ?? record.status
  return s === 3 || s === 23
}

const showReschedule = computed(() => {
  if (props.activeKey !== 1 || !props.record?.rowSpan) return false
  const os = props.record.orderStatus
  // 改签子单(22)：在改签单行显示「改签」，非原订单
  if (os === 22) {
    return !!props.record.hasReschedulablePassenger
  }
  if (os === 34 || os === 38) {
    return !!props.record.hasReschedulablePassenger
  }
  // 部分退票(11)、已出票(3)、退票中(35)：仍有已出票乘客时显示总改签按钮
  if (os === 11 || os === 3 || os === 35) {
    return !!props.record.hasReschedulablePassenger
  }
  if (os === 31 || os === 32 || os === 12) return false
  return false
})

const rescheduleModalVisible = ref(false)
const rulesModalVisible = ref(false)

const springFestivalPeriodText = computed(() =>
  formatSpringFestivalRangesText(getSpringFestivalRanges())
)

const handleReschedule = () => {
  rescheduleModalVisible.value = true
}

const confirmReschedule = () => {
  rescheduleModalVisible.value = false
  // 跳转到车票查询页面，带上原订单信息
  const query = {
    orderSn: props.record?.orderSn,
    fromStation: props.record?.departure,
    toStation: props.record?.arrival,
    departureDate: props.record?.ridingDate,
    isReschedule: '1'
  }

  router.push({
    path: '/ticketSearch',
    query
  })
}

const showRescheduleRules = () => {
  rulesModalVisible.value = true
}
</script>

<style lang="scss" scoped>
.order-status-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.extra-actions {
  line-height: 1.4;
  display: inline-flex;
  align-items: center;
}
.serial-link {
  font-size: 12px;
  text-decoration: underline;
  color: #1d4ed8;
}
.pop-line {
  margin-bottom: 6px;
  font-size: 13px;
  &.muted {
    color: #64748b;
    font-size: 12px;
    line-height: 1.5;
  }
}

.reschedule-confirm-modal {
  .modal-icon {
    text-align: center;
    margin-bottom: 20px;
  }

  .modal-content {
    margin-bottom: 24px;

    .modal-text {
      font-size: 15px;
      color: #333;
      line-height: 1.8;
      margin-bottom: 16px;
    }

    .modal-warning {
      font-size: 14px;
      color: #ff4d4f;
      line-height: 1.8;
    }
  }

  .modal-actions {
    text-align: center;
    display: flex;
    justify-content: center;
    gap: 12px;
  }
}
</style>
